const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const readCount = (v: unknown): number | undefined => {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.length > 0) {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
};

const readRegion = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" && v.length > 0) return v;
  if (typeof v === "number") return String(v);
  return "—";
};

const monthKeyCandidates = (proLoyaltyJoinedAt: string, proMonthDataKey: string): string[] => {
  const explicit = proMonthDataKey.trim();
  if (explicit.length > 0) {
    return [explicit];
  }
  return [`${proLoyaltyJoinedAt}.month`, proLoyaltyJoinedAt];
};

const pickMeasureKey = (row: Record<string, unknown>, proCountMeasure: string): string | undefined => {
  if (proCountMeasure in row) {
    return proCountMeasure;
  }
  return Object.keys(row).find((k) => k.includes("pro_count")) ?? Object.keys(row).find((k) => k === proCountMeasure);
};

const pickMonthKey = (row: Record<string, unknown>, candidates: string[], proLoyaltyJoinedAt: string): string | undefined => {
  for (const c of candidates) {
    if (c in row) {
      return c;
    }
  }
  for (const k of Object.keys(row)) {
    if (k === proLoyaltyJoinedAt) {
      return k;
    }
  }
  for (const k of Object.keys(row)) {
    if (k.toLowerCase().includes("month")) {
      return k;
    }
  }
  return undefined;
};

export type ProContractorRow = {
  monthLabel: string;
  region: string;
  count: number;
};

const formatMonthLabel = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") {
    if (v.length >= 7 && v[4] === "-") {
      return v.slice(0, 7);
    }
    return v;
  }
  if (v instanceof Date) {
    const y = v.getFullYear();
    const m = v.getMonth() + 1;
    return `${y}-${m.toString().padStart(2, "0")}`;
  }
  return String(v);
};

export const parseProContractorRows = (params: {
  rows: unknown[];
  proCountMeasure: string;
  proLoyaltyJoinedAt: string;
  proRegionDimension: string;
  proMonthDataKey: string;
}): ProContractorRow[] => {
  const { rows, proCountMeasure, proLoyaltyJoinedAt, proRegionDimension, proMonthDataKey } = params;
  const out: ProContractorRow[] = [];
  const monthCands = monthKeyCandidates(proLoyaltyJoinedAt, proMonthDataKey);
  for (const item of rows) {
    if (!isRecord(item)) {
      continue;
    }
    const mKey = pickMonthKey(item, monthCands, proLoyaltyJoinedAt);
    const measureK = pickMeasureKey(item, proCountMeasure);
    if (mKey === undefined || measureK === undefined) {
      continue;
    }
    if (!(proRegionDimension in item)) {
      continue;
    }
    const count = readCount(item[measureK]);
    if (count === undefined) {
      continue;
    }
    out.push({
      monthLabel: formatMonthLabel(item[mKey]),
      region: readRegion(item[proRegionDimension]),
      count,
    });
  }
  return out;
};
